import { Constants } from "Mention/Scripts/TFS.Mention";
import * as Mention_People_Async from "Mention/Scripts/TFS.Mention.People";
import * as Mention_PullRequest_Async from "Mention/Scripts/TFS.Mention.PullRequest";
import * as Mention_WorkItems_Async from "Mention/Scripts/TFS.Mention.WorkItems";
import { IWorkItemMentionRenderOptions } from "Mention/Scripts/WorkItem/WorkItemMentionModels";
import * as VSS from "VSS/VSS";
import * as WITOM from "WorkItemTracking/Scripts/TFS.WorkItemTracking";
import { sanitizeExternalLinks } from "WorkItemTracking/Scripts/Utils/CommentUtils";
import { vsUrlprocessHtml } from "WorkItemTracking/Scripts/Utils/VsUrlProcessor";

/**
 * Mention rendering function used in the HtmlEditorMentionPlugin
 * @param content Text content to render
 * @param enableContactCard If true, renders contact card on identity mentions
 * @param renderOptions Render options for work item mentions
 */
export function richTextFilterContentAndPreRender(
    content: Element,
    enableContactCard: boolean,
    renderOptions?: IWorkItemMentionRenderOptions,
    shouldSanitizeExternalLinks?: boolean
): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        VSS.using(
            ["Mention/Scripts/TFS.Mention.WorkItems", "Mention/Scripts/TFS.Mention.People", "Mention/Scripts/TFS.Mention.PullRequest"],
            (
                MentionWorkItems: typeof Mention_WorkItems_Async,
                MentionPeople: typeof Mention_People_Async,
                MentionPullRequest: typeof Mention_PullRequest_Async
            ) => {
                const $content = $(content);
                // filter content
                MentionWorkItems.WorkItemMentionProcessor.filterHtml($content);

                // pre render processor
                vsUrlprocessHtml($content);
                shouldSanitizeExternalLinks && sanitizeExternalLinks($content);

                MentionPullRequest.PullRequestLinkProcessor.processHtml($content);
                const peoplePromise = MentionPeople.PeopleMentionProcessor.processHtml($content, enableContactCard);
                const workItemsPromise = MentionWorkItems.WorkItemMentionProcessor.processHtml($content, renderOptions);
                Promise.all([peoplePromise, workItemsPromise])
                    .then(() => {
                        resolve($content.html());
                    })
                    .catch((e) => reject(e));
            },
            reject
        );
    });
}

export function richTextFilterContent(content: string): Promise<string> {
    return new Promise<string>((resolve) => {
        const $content = $("<div/>").append(content);
        VSS.using(["Mention/Scripts/TFS.Mention.WorkItems"], (MentionWorkItems: typeof Mention_WorkItems_Async) => {
            MentionWorkItems.WorkItemMentionProcessor.filterHtml($content);
            resolve($content.html());
        });
    });
}

export function processHtmlWithMentions(htmlContent: string, contentDiv: HTMLDivElement, renderOptions?: IWorkItemMentionRenderOptions) {
    richTextFilterContent(htmlContent).then((filteredContent) => {
        const $container = $(contentDiv);
        $container.html(filteredContent);
        richTextPreRenderProcessor($container, true, renderOptions);
    });
}

export function richTextPreRenderProcessor($container: JQuery, enableContactCard: boolean, renderOptions?: IWorkItemMentionRenderOptions) {
    VSS.using(
        ["Mention/Scripts/TFS.Mention.WorkItems", "Mention/Scripts/TFS.Mention.People", "Mention/Scripts/TFS.Mention.PullRequest"],
        (
            MentionWorkItems: typeof Mention_WorkItems_Async,
            MentionPeople: typeof Mention_People_Async,
            MentionPullRequest: typeof Mention_PullRequest_Async
        ) => {
            MentionPeople.PeopleMentionProcessor.processHtml($container, enableContactCard);
            MentionWorkItems.WorkItemMentionProcessor.processHtml($container, renderOptions);
            MentionPullRequest.PullRequestLinkProcessor.processHtml($container);
            vsUrlprocessHtml($container);
            sanitizeExternalLinks($container);
        }
    );
}

export function richTextPreRenderProcessorWithPeopleMentions(
    $container: JQuery,
    enableContactCard: boolean,
    renderOptions?: IWorkItemMentionRenderOptions
): Promise<Mention_People_Async.PeopleMentionRendererHTMLComponent[]> {
    return new Promise((resolve) => {
        VSS.using(
            ["Mention/Scripts/TFS.Mention.WorkItems", "Mention/Scripts/TFS.Mention.People", "Mention/Scripts/TFS.Mention.PullRequest"],
            (
                MentionWorkItems: typeof Mention_WorkItems_Async,
                MentionPeople: typeof Mention_People_Async,
                MentionPullRequest: typeof Mention_PullRequest_Async
            ) => {
                MentionPeople.PeopleMentionProcessor.processHtmlBatch($container, enableContactCard).then(
                    (peopleMentions: Mention_People_Async.PeopleMentionRendererHTMLComponent[]) => {
                        resolve(peopleMentions);
                    }
                );
                MentionWorkItems.WorkItemMentionProcessor.processHtml($container, renderOptions);
                MentionPullRequest.PullRequestLinkProcessor.processHtml($container);
                vsUrlprocessHtml($container);
                sanitizeExternalLinks($container);
            }
        );
    });
}

export function normalizeHtmlValue(message: string) {
    const additionalValidAttributes = [Constants.HTML_MENTION_ATTR_NAME, Constants.HTML_RENDERED_MENTION_ATTR_NAME];

    const hasRenderedMentions = message.indexOf(Constants.HTML_RENDERED_MENTION_ATTR_NAME) >= 0;
    if (hasRenderedMentions) {
        additionalValidAttributes.push("class"); // we'll remove class ourself
    }

    let result = WITOM.Field.normalizeHtmlValue(message, additionalValidAttributes, ["height", "white-space", "word-wrap", "float"]);

    // only keep class attribute for mentions
    if (hasRenderedMentions) {
        const $resultDiv = $("<div/>").html(result);
        $resultDiv
            .find("[class]")
            .filter(function () {
                return $(this).closest(`[${Constants.HTML_RENDERED_MENTION_ATTR_NAME}]`).length === 0;
            })
            .removeAttr("class");
        result = $resultDiv.html();
    }

    // normalizeHtmlValue call above sets href to empty so, set href back to '#' for people mentions to avoid issues with
    // RichTextPersonMentionParser from detecting mentions
    if (message.indexOf(Constants.HTML_MENTION_ATTR_NAME) !== -1) {
        const $resultDiv = $("<div/>").html(result);
        $resultDiv.find(`a[${Constants.HTML_MENTION_ATTR_NAME}][href='']`).attr("href", "#");

        result = $resultDiv.html();
    }

    return result;
}
